import type { AxiosError } from 'axios';
import { take } from 'redux-saga-test-plan/matchers';
import { all, call, getContext, put, race, select, takeEvery } from 'redux-saga/effects';
import type { Action } from '../utils';
import type { ICustomerBase, IEmployee, IMetadata, IRelationshipManager } from '../../api/domain';
import type { CustomerBaseApiService, EmployeeMasterDataApiService } from '../../api/services';
import { hideProgressIndicator, setUserIsProspect, showProgressIndicator } from '../application/application-actions';
import {
    fetchBranch,
    fetchBranchFailure,
    fetchBranchSuccess,
    fetchTeam,
    fetchTeamFailure,
    fetchTeamSuccess,
} from '../employee-master-data-api/employee-master-data-actions';
import { logError } from '../logging-api/logging-api-actions';
import {
    fetchUserPortalAccountSuccess,
    updateOrAddSingleAccountMetadata,
    updateOrAddSingleAccountMetadataFailure,
    updateOrAddSingleAccountMetadataSuccess,
} from '../portal-account-api/portal-account-api-actions';
import { getMetadataByKey } from '../portal-account-api/portal-account-api-selectors';
import {
    fetchCustomerBases,
    fetchCustomerBasesFailure,
    fetchCustomerBasesSuccess,
    readDefaultCustomerBase,
    readDefaultCustomerBaseFailure,
    readDefaultCustomerBaseSuccess,
    resolveRmForCustomerBase,
    resolveRmForCustomerBaseFailure,
    resolveRmForCustomerBaseSuccess,
    setDefaultCustomerBase,
    setDefaultCustomerBaseFailure,
    setDefaultCustomerBaseSuccess,
    updateCustomerBases,
} from './customer-base-api-actions';
import { getCustomerBases } from './customer-base-api-selectors';

export const DEFAULT_CUSTOMER_BASE_METADATA_KEY = 'default-customer-base';
export const DEFAULT_CUSTOMER_BASE_METADATA_CATEGORY = 'customer-attributes';

function* handleFetchCustomerBases() {
    const api: CustomerBaseApiService = yield getContext('customerBaseApiService');

    try {
        const customerBases: ICustomerBase[] = yield call([api, api.getCustomerBases]);
        yield put(fetchCustomerBasesSuccess(customerBases));
    } catch (error: unknown) {
        if ((error as AxiosError)?.response?.status === 423) {
            yield put(setUserIsProspect());
            yield put(fetchCustomerBasesSuccess([]));
        } else {
            yield put(logError(`could not fetch customer bases: ${error}`));
            yield put(fetchCustomerBasesFailure());
        }
    }
}

function* handleResolveRmForCustomerBase({ payload: customerBase }: Action<ICustomerBase>) {
    const emdApi: EmployeeMasterDataApiService = yield getContext('employeeMasterDataApiService');

    try {
        yield put(showProgressIndicator());
        if (!customerBase) {
            throw new Error('Customerbase is undefined');
        }
        if (customerBase.relationshipManager) {
            yield put(resolveRmForCustomerBaseSuccess(customerBase.relationshipManager));

            return;
        }

        const [employee]: IEmployee[] = yield call([emdApi, emdApi.getEmployeesByExternalIds], [customerBase.relationshipManagerId]);
        const customerBases: ICustomerBase[] = yield select(getCustomerBases);
        const otherCustomerBases = customerBases.filter((cb) => cb.externalId !== customerBase.externalId);

        if (!employee) {
            throw new Error('Relationship manager not found');
        }

        const relationshipManager: IRelationshipManager = {
            id: employee.externalId,
            employee,
        };

        if (employee.mainTeamExternalId) {
            yield put(fetchTeam(employee.mainTeamExternalId));
            const { teamSuccess } = yield race({
                teamSuccess: take(fetchTeamSuccess),
                teamFailure: take(fetchTeamFailure),
            });

            const team = teamSuccess?.payload;
            const teamBranchId = team?.branchExternalId;
            relationshipManager.team = team;

            yield put(fetchBranch(`${teamBranchId}`));
            const { branchSuccess } = yield race({
                branchSuccess: take(fetchBranchSuccess),
                branchFailure: take(fetchBranchFailure),
            });

            relationshipManager.branch = branchSuccess?.payload;
        }
        yield put(resolveRmForCustomerBaseSuccess(relationshipManager));

        const customerBaseCopy = { ...customerBase };
        customerBaseCopy.relationshipManager = relationshipManager;

        yield put(updateCustomerBases([...otherCustomerBases, customerBaseCopy]));
    } catch (error: unknown) {
        yield put(logError(`Could not fetch relationship manager for customer base: ${error}`));
        yield put(resolveRmForCustomerBaseFailure());
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleReadDefaultCustomerBase() {
    try {
        const customerBases: ICustomerBase[] = yield select(getCustomerBases);
        const defaultCustomerBaseMetadata: IMetadata | undefined = yield select(getMetadataByKey, DEFAULT_CUSTOMER_BASE_METADATA_KEY);
        yield put(
            readDefaultCustomerBaseSuccess(
                defaultCustomerBaseMetadata && customerBases.find((customerBase) => customerBase.externalId === defaultCustomerBaseMetadata.value),
            ),
        );
    } catch (error: unknown) {
        yield put(logError(`could not read default customer base: ${error}`));
        yield put(readDefaultCustomerBaseFailure());
    }
}

export function* handleSetDefaultCustomerBase({ payload }: Action<string | undefined>) {
    try {
        yield put(showProgressIndicator());
        const metadata: IMetadata = {
            key: DEFAULT_CUSTOMER_BASE_METADATA_KEY,
            category: DEFAULT_CUSTOMER_BASE_METADATA_CATEGORY,
            value: payload,
        };
        yield put(updateOrAddSingleAccountMetadata(metadata));
        yield take([updateOrAddSingleAccountMetadataSuccess, updateOrAddSingleAccountMetadataFailure]);
        yield put(setDefaultCustomerBaseSuccess());
    } catch (error: unknown) {
        yield put(logError(`could not set default customer base: ${error}`));
        yield put(setDefaultCustomerBaseFailure());
    } finally {
        yield put(hideProgressIndicator());
    }
}

export function* customerBaseApiSaga() {
    yield all([
        takeEvery(fetchCustomerBases, handleFetchCustomerBases),
        takeEvery(setDefaultCustomerBase, handleSetDefaultCustomerBase),
        takeEvery(fetchUserPortalAccountSuccess, handleReadDefaultCustomerBase),
        takeEvery(readDefaultCustomerBase, handleReadDefaultCustomerBase),
        takeEvery(resolveRmForCustomerBase, handleResolveRmForCustomerBase),
    ]);
}
